export interface RecentProject {
  name: string;
  path: string;
  lastOpened: string;
  dateCreated: string;
}

const MAX_RECENT_PROJECTS = 5;

export async function addRecentProject(
  name: string,
  path: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const isElectron = typeof window !== "undefined" && window.electronAPI;

    if (isElectron) {
      try {
        const result = await window.electronAPI.addRecentProject({
          name,
          path,
        });
        console.log("Added to recent projects (Electron):", result);
        return result;
      } catch (electronError) {
        console.error(
          "Electron recent projects failed, using localStorage fallback:",
          electronError
        );
        // Fall back to localStorage if Electron fails
      }
    }

    // Browser mode OR Electron fallback
    const projects = await getRecentProjects();

    // Normalize path for comparison (remove trailing slashes, lowercase)
    const normalizedPath = path.trim().replace(/\/+$/, "").toLowerCase();

    const now = new Date().toISOString();

    // Find existing project with normalized path comparison
    const existingIndex = projects.findIndex(
      (p) => p.path.trim().replace(/\/+$/, "").toLowerCase() === normalizedPath
    );

    if (existingIndex !== -1) {
      // Update existing entry
      console.log("Updating existing recent project:", name);
      projects[existingIndex].lastOpened = now;
      projects[existingIndex].name = name;
      projects[existingIndex].path = path; // Update to latest path format
    } else {
      // Add new entry
      console.log("Adding new recent project:", name);
      const newProject: RecentProject = {
        name,
        path,
        lastOpened: now,
        dateCreated: now,
      };

      projects.unshift(newProject);

      // Limit to max recent projects
      if (projects.length > MAX_RECENT_PROJECTS) {
        projects.pop();
      }
    }

    // Sort by lastOpened (most recent first)
    projects.sort(
      (a, b) =>
        new Date(b.lastOpened).getTime() - new Date(a.lastOpened).getTime()
    );

    // Additional deduplication check before saving (extra safety)
    const uniqueProjects = projects.filter(
      (project, index, self) =>
        index ===
        self.findIndex(
          (p) =>
            p.path.trim().replace(/\/+$/, "").toLowerCase() ===
            project.path.trim().replace(/\/+$/, "").toLowerCase()
        )
    );

    localStorage.setItem("recentProjects", JSON.stringify(uniqueProjects));
    console.log(
      `✅ Saved ${uniqueProjects.length} unique recent projects to localStorage`
    );

    return { success: true };
  } catch (error) {
    console.error("Error adding recent project:", error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

export async function getRecentProjects(): Promise<RecentProject[]> {
  try {
    const isElectron = typeof window !== "undefined" && window.electronAPI;

    if (isElectron) {
      const result = await window.electronAPI.getRecentProjects();
      return result.success ? result.projects : [];
    } else {
      const stored = localStorage.getItem("recentProjects");
      if (!stored) return [];

      const projects: RecentProject[] = JSON.parse(stored);

      // Remove duplicates (in case they exist from old code)
      const uniqueProjects = projects.filter(
        (project, index, self) =>
          index ===
          self.findIndex(
            (p) =>
              p.path.trim().replace(/\/+$/, "").toLowerCase() ===
              project.path.trim().replace(/\/+$/, "").toLowerCase()
          )
      );

      // Sort by lastOpened
      uniqueProjects.sort(
        (a, b) =>
          new Date(b.lastOpened).getTime() - new Date(a.lastOpened).getTime()
      );

      // If we removed duplicates, save the cleaned list
      if (uniqueProjects.length !== projects.length) {
        console.log(
          `🧹 Cleaned ${projects.length - uniqueProjects.length} duplicate recent projects`
        );
        localStorage.setItem("recentProjects", JSON.stringify(uniqueProjects));
      }

      return uniqueProjects;
    }
  } catch (error) {
    console.error("Error getting recent projects:", error);
    return [];
  }
}

export async function removeRecentProject(
  path: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const isElectron = typeof window !== "undefined" && window.electronAPI;

    if (isElectron) {
      return await window.electronAPI.removeRecentProject(path);
    } else {
      const projects = await getRecentProjects();
      const filtered = projects.filter((p) => p.path !== path);
      localStorage.setItem("recentProjects", JSON.stringify(filtered));
      return { success: true };
    }
  } catch (error) {
    console.error("Error removing recent project:", error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

export async function clearRecentProjects(): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const isElectron = typeof window !== "undefined" && window.electronAPI;

    if (isElectron) {
      return await window.electronAPI.clearRecentProjects();
    } else {
      localStorage.removeItem("recentProjects");
      return { success: true };
    }
  } catch (error) {
    console.error("Error clearing recent projects:", error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

export async function projectExists(path: string): Promise<boolean> {
  try {
    const isElectron = typeof window !== "undefined" && window.electronAPI;

    if (isElectron) {
      const result = await window.electronAPI.checkProjectExists(path);
      return result.exists;
    } else {
      return true;
    }
  } catch (error) {
    console.error("Error checking project exists:", error);
    return false;
  }
}
